import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Globe,
  X,
  MapPin
} from 'lucide-react';
import UniversalTable from '../../services/universalTable';
import MultiSelectFilter from '../common/MultiSelectFilter';

// API BASE PATH
const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Progress Bar Component
const ProgressBar = ({ progress, message }) => {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="w-80 bg-gray-200 rounded-full h-3 shadow-inner">
        <div 
          className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out shadow-lg"
          style={{ width: `${progress}%` }}
        >
          <div className="h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full opacity-50 animate-pulse"></div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
        <span className="text-lg text-gray-700 font-medium">{message}</span>
      </div>
      <div className="text-sm text-blue-600 font-semibold">
        {progress}% Complete
      </div>
    </div>
  );
};

const LiveAffiliate = () => {
  const navigate = useNavigate();

  // Get brand data from Redux store
  const selectedBrand = useSelector(state => state.liveAffiliates.selectedBrand);
  const user = useSelector(state => state.auth.user); // Get user data for plan
  const brandData = selectedBrand || {};
  
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingTableData, setIsLoadingTableData] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Country filter. Which countries the affiliates shown must draw organic
  // traffic from — ANY of the picked ones. Empty means no country filter, which
  // is what the page always did. The options are this brand's own countries
  // (from /countries, with an affiliate count each), not a world list, so the
  // user is never offered a country that would empty the table.
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  // Get user plan
  const userPlan = user?.plan || 'Free';

  // Transform function to match AffiliateRecruitment structure
  const TransformDataAPI = (data) => {
    const transformedData = data.map((item, index) => {
      return {
        paid_traffic: item.adwords_traffic,
        imageUrl: item.screenshot_url || 'blurrow.png', // No plan restrictions
        paid_traffic_difference: item.adwords_traffic_difference,
        affiliate_url: item.affiliate_url,
        already_approached: item.already_approached,
        email: item.email,
        id: item.id || `affiliate-${index}`,
        keywords: item.keywords,
        organic_traffic: item.organic_traffic,
        organic_traffic_difference: item.organic_traffic_difference,
        paid_keyword: item.paid_keyword,
        adwords_keywords: item.adwords_keywords,
        paid_top_page: item.paid_top_page,
        rank: item.rank,
        rank_difference: item.rank_difference,
        top_geo_organic_traffic: item.top_geo_organic_traffic,
        top_geo_organic_traffic_difference: item.top_geo_organic_traffic_difference, 
        top_page: item.top_page,
        total_traffic: item.total_traffic,
        total_traffic_difference: item.total_traffic_difference,
        screenshot_url: item.screenshot_url,
        // Additional fields from live affiliate data
        organic_keywords: item.organic_keywords,
        organic_cost: item.organic_cost,
        adwords_cost: item.adwords_cost,
        top_geos: item.top_geos,
        top_geo_rank: item.top_geo_rank,
        vertical: item.vertical,
        sub_vertical: item.sub_vertical,
        existing_affiliate: item.existing_affiliate,
        live_affiliate_brand_url: item.live_affiliate_brand_url
      };
    });
    return transformedData;
  };

  // Fetch data from API with pagination
  const fetchAffiliates = async (page = 0, pageSize = 10, showProgress = true, search = searchQuery, countries = selectedCountries) => {
    if (!brandData.brand_url) {
      setLoading(false);
      setError('No brand selected. Please select a brand from the directory.');
      return;
    }

    if (showProgress) {
      setLoading(true);
      setLoadingProgress(0);
      setLoadingMessage('Initializing search...');
    } else {
      setIsLoadingTableData(true);
    }
    
    setError(null);
    
    try {
      // Progress point 1: Starting request
      if (showProgress) {
        setLoadingProgress(10);
        setLoadingMessage('Preparing request...');
      }
      
      const requestBody = {
        brand_url: brandData.brand_url,
        brand_name: brandData.brand_name,
        brand_id: brandData.brand_id,
        vertical: brandData.vertical,
        page: page,
        pageSize: pageSize,
        sortBy: 'organic_traffic',
        sortOrder: 'desc',
        search: search || '',
        // Country filter — see selectedCountries. Sent as names; the backend
        // matches every spelling SEMrush uses for each.
        countries: countries || [],
        // Pass gap/common affiliate filter flags
        isGapAnalysis: brandData.isGapAnalysis || false,
        isCommonAffiliates: brandData.isCommonAffiliates || false,
        userBrandUrl: brandData.userBrandUrl || null
      };

      // Progress point 2: Sending request
      if (showProgress) {
        setLoadingProgress(25);
        setLoadingMessage('Sending request to server...');
      }

      const response = await fetch(`${basePath}/api/live-affiliate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      // Progress point 3: Received response
      if (showProgress) {
        setLoadingProgress(50);
        setLoadingMessage('Processing server response...');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Progress point 4: Parsing data
      if (showProgress) {
        setLoadingProgress(70);
        setLoadingMessage('Parsing affiliate data...');
      }

      const responseData = await response.json();
      
      // Progress point 5: Processing data
      if (showProgress) {
        setLoadingProgress(85);
        setLoadingMessage('Organizing affiliates...');
      }
      
      let affiliatesData = [];
      
      if (responseData.success) {
        // Handle successful response structure
        if (Array.isArray(responseData.data)) {
          affiliatesData = responseData.data;
        } else if (responseData.affiliates && Array.isArray(responseData.affiliates)) {
          affiliatesData = responseData.affiliates;
        } else {
          affiliatesData = [];
        }
        
        // Update pagination info from response
        setTotalItems(responseData.total || 0);
        setTotalPages(responseData.totalPages || 0);
        setCurrentPage(responseData.page || 0);
        setCurrentPageSize(responseData.pageSize || pageSize);
      } else {
        // Handle legacy structure
        if (Array.isArray(responseData)) {
          affiliatesData = responseData;
        } else if (responseData.affiliates && Array.isArray(responseData.affiliates)) {
          affiliatesData = responseData.affiliates;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          affiliatesData = responseData.data;
        } else if (responseData.results && Array.isArray(responseData.results)) {
          affiliatesData = responseData.results;
        } else {
          const arrayProps = Object.keys(responseData).filter(key => Array.isArray(responseData[key]));
          if (arrayProps.length > 0) {
            affiliatesData = responseData[arrayProps[0]];
          } else {
            affiliatesData = [];
          }
        }
      }
      
      // Progress point 6: Finalizing
      if (showProgress) {
        setLoadingProgress(95);
        setLoadingMessage('Finalizing results...');
      }
      
      // Transform the data to match AffiliateRecruitment structure
      const transformedData = TransformDataAPI(affiliatesData);
      
      // Progress point 7: Complete
      if (showProgress) {
        setLoadingProgress(100);
        setLoadingMessage('Complete!');
        
        // Small delay to show completion
        setTimeout(() => {
          setAffiliates(transformedData);
          setLoading(false);
        }, 500);
      } else {
        setAffiliates(transformedData);
        setIsLoadingTableData(false);
      }
      
    } catch (err) {
      console.error('Error fetching affiliates:', err);
      setError(err.message || 'Failed to fetch affiliates');
      setLoading(false);
      setIsLoadingTableData(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    fetchAffiliates(newPage, currentPageSize, false);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setCurrentPage(0);
    fetchAffiliates(0, newPageSize, false);
  };

  // Handle server-side search — resets to page 0 and fetches with new query
  const handleSearch = (term) => {
    setSearchQuery(term);
    setCurrentPage(0);
    fetchAffiliates(0, currentPageSize, false, term);
  };

  // Country pick changed — same as a search: back to page 0, table-only loader.
  const handleCountriesChange = (countries) => {
    setSelectedCountries(countries);
    setCurrentPage(0);
    fetchAffiliates(0, currentPageSize, false, searchQuery, countries);
  };

  // The countries this brand's affiliates (in the card's gap/common scope)
  // have traffic from. Fetched once per brand, alongside the first page, and
  // never narrowed by the current pick — the picker has to keep offering what
  // the user could add. A failure leaves the options empty, which hides the
  // filter rather than offering a box that cannot answer.
  const fetchCountryOptions = async () => {
    if (!brandData.brand_url) return;
    try {
      const response = await fetch(`${basePath}/api/live-affiliate/countries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          brand_url: brandData.brand_url,
          isGapAnalysis: brandData.isGapAnalysis || false,
          isCommonAffiliates: brandData.isCommonAffiliates || false,
          userBrandUrl: brandData.userBrandUrl || null
        })
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const responseData = await response.json();
      setCountryOptions(responseData.success && Array.isArray(responseData.data) ? responseData.data : []);
    } catch (err) {
      console.error('Error fetching country options:', err);
      setCountryOptions([]);
    }
  };

  // Initial fetch. A new brand starts with no country filter: the previous
  // brand's countries may not exist for this one.
  useEffect(() => {
    setSelectedCountries([]);
    setCountryOptions([]);
    fetchAffiliates(0, 10, true, searchQuery, []);
    fetchCountryOptions();
  }, [brandData.brand_url, brandData.brand_name, brandData.brand_id, brandData.vertical]);

  // Picker options: name is what the user reads and what the filter sends;
  // code drives the flag (null for a name the code list does not know).
  const countryFilterOptions = countryOptions.map(country => ({
    name: country.name,
    value: country.name,
    code: country.code,
    affiliates: country.affiliates
  }));

  // The Countries picker for the table toolbar, next to the search box. Only
  // once /countries has answered with something, so the toolbar looks exactly
  // as it did before on an older backend or for a brand with no geo data.
  const countryFilter = countryFilterOptions.length > 0 ? (
    <MultiSelectFilter
      compact
      label="Countries"
      options={countryFilterOptions}
      selectedValues={selectedCountries}
      onChange={handleCountriesChange}
      renderOption={(country) => (
        <div className="flex items-center gap-2 min-w-0 w-full">
          {country.code ? (
            <img
              src={`https://flagsapi.com/${country.code}/flat/24.png`}
              alt={country.name}
              className="w-6 h-4 flex-shrink-0"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="16" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23ddd"/></svg>';
              }}
            />
          ) : (
            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
          <span className="text-sm truncate">{country.name}</span>
          <span className="ml-auto text-xs text-gray-400 dark:text-slate-500 flex-shrink-0">
            {country.affiliates}
          </span>
        </div>
      )}
    />
  ) : null;

  // Prepare pagination info for UniversalTable
  const paginationInfo = {
    currentPage: currentPage,
    page: currentPage,
    totalPages: totalPages,
    totalItems: totalItems,
    total: totalItems,
    pageSize: currentPageSize,
    hasNextPage: currentPage + 1 < totalPages,
    hasPreviousPage: currentPage > 0
  };

  // Use affiliates as filteredData (no client-side filtering needed since server handles pagination)
  const filteredData = affiliates;

  // Show full page loader while loading data
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <ProgressBar progress={loadingProgress} message={loadingMessage} />
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-slate-950 flex items-center justify-center">
        <div className="bg-white dark:bg-slate-900 p-8 rounded-xl border border-red-200 dark:border-red-900 shadow-sm max-w-md">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
              <span className="text-2xl text-red-600 dark:text-red-400">!</span>
            </div>
            <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100">Error Loading Data</h3>
            <p className="text-gray-600 dark:text-slate-400 text-center">{error}</p>
            {brandData.brand_url ? (
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            ) : (
              <button 
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go Back
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950">

      {/* Header section */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
            <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100">
  Live Affiliate
  {brandData.brand_name && (
    <span className="text-blue-600">
      {' '}For {brandData.brand_name
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')}
    </span>
  )}
</h1>

              <div className="space-y-1">
                {brandData.brand_url && (
                  <p className="text-gray-600 dark:text-slate-400 leading-relaxed">
                    Brand: {brandData.brand_url}
                  </p>
                )}
                <p className="text-gray-600 dark:text-slate-400 leading-relaxed">
                  Live affiliate data for the selected brand
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-6">
          <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <X className="h-5 w-5 text-red-400 dark:text-red-300" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results section using UniversalTable */}
      {affiliates.length > 0 || isLoadingTableData ? (
       <UniversalTable
          data={filteredData}
          paginationInfo={paginationInfo}
          onFetchData={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          toolbarControls={countryFilter}
          title="Live Affiliate"
          searchable={true}
          defaultPageSize={currentPageSize}
          loading={isLoadingTableData}
          userPlan={userPlan}
          // These are this brand's affiliates, so the Vertical column shows the
          // brand's own vertical rather than every vertical the site has ever
          // been tagged with.
          brandVertical={brandData.vertical}
        />
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-8">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-gray-400 dark:text-slate-500 mb-4">
              <Globe className="w-16 h-16" />
            </div>
            <p className="text-gray-600 dark:text-slate-400 text-lg">No affiliates found</p>
            {selectedCountries.length > 0 ? (
              <>
                <p className="text-gray-500 dark:text-slate-500 text-sm mt-2">
                  None of this brand's affiliates have traffic from {selectedCountries.join(', ')}
                </p>
                <div className="mt-4 flex items-center justify-center gap-3">
                  {countryFilter}
                  <button
                    type="button"
                    onClick={() => handleCountriesChange([])}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Clear country filter
                  </button>
                </div>
              </>
            ) : (
              <p className="text-gray-500 dark:text-slate-500 text-sm mt-2">Try selecting a different brand</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveAffiliate;